import {useQuery, UseQueryResult} from '@tanstack/react-query';
import {useContext} from 'react';

import {useReviewsAnalysisFilter} from '../../../page/main/reviews-analysis/reviews-analysis-hook';
import {MainFilterContext} from '../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../provider/main-filter/main-filter-type';
import {useTableSorter} from '../../../util/antd/table/table-sorter-hook';
import {usePagination} from '../../../util/pagination-hook/pagination-hook';
import {PaginationOptionsType} from '../../../util/pagination-hook/pagination-hook-type';

import {
    fetchReviewsAnalysisAspects,
    fetchReviewsAnalysisAspectsTone,
    fetchReviewsAnalysisAspectsToneDistribution,
    fetchReviewsCountByAspects,
    fetchTopAspects,
} from './review-aspects-api';
import {
    AspectsAnalysisRequestParametersType,
    ReviewAnalyticsAspectsHookType,
    ReviewAspectsToneDistributionType,
    ReviewAspectsToneType,
    ReviewsCountByAspectsType,
    TopAspectsItemType,
    TopAspectsQueryParametersType,
} from './review-aspects-type';

function useAspectsAnalysisRequestParameters(): AspectsAnalysisRequestParametersType {
    const {comparePeriodFilter, singleFilter} = useReviewsAnalysisFilter(false);

    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    return {
        ...singleFilter,
        comparedStartDate: comparePeriodFilter.startDate,
        comparedEndDate: comparePeriodFilter.endDate,
        filterKey: mainFilterKey,
    };
}

export function useReviewAnalyticsAspects(paginationOption?: PaginationOptionsType): ReviewAnalyticsAspectsHookType {
    const sorter = useTableSorter();
    const {sorterQueryValue} = sorter;

    const requestOptions = useAspectsAnalysisRequestParameters();

    const pagination = usePagination({
        dependencies: null,
        shouldSaveState: false,
        ...paginationOption,
    });

    const query = useQuery(
        ['review-analysis-aspects', pagination, requestOptions, sorterQueryValue],
        () =>
            fetchReviewsAnalysisAspects({
                sortQueryValue: sorterQueryValue,
                ...requestOptions,
                ...pagination,
            }),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            onSuccess: (data) => {
                pagination.onDataLoaded(data);
            },
            onError: pagination.onDataLoadFailed,
        }
    );

    return {
        ...query,
        pagination,
        sorter,
    };
}

export function useAspectsToneDynamics(): UseQueryResult<ReviewAspectsToneType> {
    const requestOptions = useAspectsAnalysisRequestParameters();

    return useQuery(['aspects-tone-dynamics', requestOptions], () => fetchReviewsAnalysisAspectsTone(requestOptions), {
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchOnMount: false,
    });
}

export function useAspectsToneDistribution(): UseQueryResult<ReviewAspectsToneDistributionType> {
    const requestOptions = useAspectsAnalysisRequestParameters();

    return useQuery(
        ['aspects-tone-distribution', requestOptions],
        () => fetchReviewsAnalysisAspectsToneDistribution(requestOptions),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            refetchOnMount: false,
        }
    );
}

export function useReviewsCountByAspects(): UseQueryResult<ReviewsCountByAspectsType> {
    const requestOptions = useAspectsAnalysisRequestParameters();

    return useQuery(['reviews-count-by-aspects', requestOptions], () => fetchReviewsCountByAspects(requestOptions), {
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchOnMount: false,
    });
}

export function useTopAspects(
    filterParameters: TopAspectsQueryParametersType
): UseQueryResult<Array<TopAspectsItemType>> {
    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    return useQuery(
        ['top-aspects', mainFilterKey, filterParameters],
        () => fetchTopAspects(mainFilterKey, filterParameters),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
        }
    );
}
